import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { State, tabActions } from '@store/index';
import { Loader } from '@common/hooks/useLoader';
import { useWorkflow } from '@common/hooks/useWorkflow';
import { listWorkflows } from '@requests/flow';
import { LayoutFlow, prefetchWorkspaceQueries } from '@common/layout';
import { TopToolbar } from './Toolbar/Top';
import { BottomToolbar } from './Toolbar';
import { Canvas } from './Canvas';
import { Flow as FlowType } from '@store/async';
import { Button } from '@common/Button';
import { Resource } from '@store/types';
import { usePermissions } from '@common/hooks/usePermissions';
import { RequestError } from '@requests/common';

const Container = styled.div`
  display: grid;
  grid-template-rows: repeat(1fr);
  grid-auto-rows: max-content;
  width: 100%;
  background-color: ${({ theme }) => theme.background.standard};
`;

const Size = styled.div<{ nodeSelected: boolean; trace?: boolean }>`
  height: ${({ nodeSelected, trace }) => getCanvasHeight(nodeSelected, trace)};
  border-top: 1px solid ${({ theme }) => theme.line.light};
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  border-top: 1px solid ${({ theme }) => theme.line.light};
  height: calc((100vh - 64px) - 60px - 3rem);
  background-color: ${({ theme }) => theme.background.standard};
  padding-top: 3rem;
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  border-top: 1px solid ${({ theme }) => theme.line.light};
  background-color: ${({ theme }) => theme.background.standard};
  padding: 0 8rem;
  padding-top: 3rem;

  & .MuiAlert-message {
    white-space: pre;
  }
`;

const ReadOnlyBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.telnyx.yellow};
  color: white;
  font-size: 13px;
  font-weight: bold;
  height: 20px;
  width: 100%;

  svg {
    font-size: 14px;
    margin-right: 4px;
  }
`;

const getCanvasHeight = (nodeSelected: boolean, trace = false) => {
  // ((page height - navbar height) - top toolbar height - bottom toolbar height
  if (nodeSelected && trace) return `calc((100vh - 64px) - 36px - 60px - 14px)`;
  if (nodeSelected) return `calc((100vh - 64px) - 36px - 60px)`;

  return `calc((100vh - 64px) - 36px)`;
};

export const Flow: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { service_id, workflow_id } = useParams();
  const { getWorkflow } = useWorkflow();
  const { admin, permissions } = usePermissions(service_id as string, Resource.WORKFLOW);

  const abortControllerRef = useRef<AbortController | null>(null);
  const selectedNode = useSelector((state: State) => state.tabs.active.selectedNode);
  const error = useSelector((state: State) => state.tabs.active.error);
  const isNewFlow = useSelector((state: State) => state.tabs.active.new);
  const isUnstoredFlow = useSelector((state: State) => state.tabs.active.unstored);
  const tabs = useSelector((state: State) => state.tabs.list);

  const activeTabFlowId = useSelector((state: State) => state.tabs.active.flow_id);
  const activeTabLoading = useSelector((state: State) => state.tabs.active.loading);
  const traceId = useSelector((state: State) => state.tabs.active.run.id);
  const traceRunning = useSelector((state: State) => state.tabs.active.run.running);
  const isUnstoredTab = tabs?.some((tab) => tab?.flow?.flow_id === workflow_id);

  const allowCreate = admin || permissions?.create;
  const isTraceRunning = traceId || traceRunning;

  const {
    data: workflows = [],
    isLoading: workflowLoading,
    error: workflowsError,
  } = useQuery<FlowType[], RequestError, FlowType[], [string, string | undefined]>({
    queryKey: ['workflows', service_id],
    queryFn: listWorkflows,
  });

  const workflow = workflows?.find((flow: FlowType) => flow.flow_id === workflow_id);

  const flowNotFound = () => {
    navigate(`/${service_id}/workflow`);
  };

  const isLoading = (activeTabLoading && workflow_id === activeTabFlowId) || workflowLoading;

  // loads the flow into the global store when the canvas is rendered
  useEffect(() => {
    if (workflowLoading || !workflows?.length) return;
    if (workflowsError) return;
    if (!workflow && !(isNewFlow || isUnstoredFlow || isUnstoredTab)) return flowNotFound();
    if (workflow) getWorkflow(workflow_id as string, workflow.name);
  }, [workflows, workflow_id, workflowsError]);

  useEffect(() => {
    prefetchWorkspaceQueries(service_id, queryClient);
  }, [service_id, queryClient]);

  if (error)
    return (
      <LayoutFlow
        abortControllerRef={abortControllerRef}
        body={
          <Container>
            <TopToolbar />
            <ErrorContainer>
              <Alert
                sx={{ width: '100%' }}
                action={
                  <Button
                    customColor='red'
                    onClick={() => {
                      navigate(`/${service_id}/workflow`);
                      dispatch(tabActions.removeTab(activeTabFlowId || ''));
                    }}
                  >
                    Close Workflow
                  </Button>
                }
                severity='error'
              >
                {error}
              </Alert>
            </ErrorContainer>
          </Container>
        }
      />
    );

  if (isLoading)
    return (
      <LayoutFlow
        abortControllerRef={abortControllerRef}
        body={
          <Container>
            <TopToolbar />
            <LoadingContainer>
              <Loader />
            </LoadingContainer>
          </Container>
        }
      />
    );

  return (
    <LayoutFlow
      abortControllerRef={abortControllerRef}
      body={
        <Container>
          <TopToolbar />
          {!allowCreate && (
            <ReadOnlyBanner>
              {/* <WarningAmberOutlined /> */}
              READ ONLY (changes cant be saved)
            </ReadOnlyBanner>
          )}
          {/* {traceRunning && <CanvasBanner trace={trace} />} */}
          <Size nodeSelected={selectedNode !== null && !isTraceRunning} trace={false}>
            <Canvas abortControllerRef={abortControllerRef} />
          </Size>
          {selectedNode && !isTraceRunning && <BottomToolbar />}
        </Container>
      }
    />
  );
};
